import { Box, Button, Grid, Stack, Typography, useMediaQuery, Container, Card } from "@mui/material";
import React, { useEffect, useState } from "react";
import Slider from "react-slick";
import dayjs from 'dayjs';
import instId from "../network/instituteId";
import Network from "../network/Network";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import StarIcon from '@mui/icons-material/Star';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';

// Add CSS animations
const globalStyles = `
    @keyframes fadeInUp {
        from {
            opacity: 0;
            transform: translateY(50px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
    
    @keyframes slideInLeft {
        from {
            opacity: 0;
            transform: translateX(-80px);
        }
        to {
            opacity: 1;
            transform: translateX(0);
        }
    }
    
    @keyframes pulse {
        0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(102, 126, 234, 0.7);
        }
        50% {
            transform: scale(1.05);
            box-shadow: 0 0 0 10px rgba(102, 126, 234, 0);
        }
    }
    
    @keyframes float {
        0%, 100% {
            transform: translateY(0px) rotate(0deg);
        }
        50% {
            transform: translateY(-15px) rotate(5deg);
        }
    }
    
    @keyframes shimmer {
        0% {
            background-position: -1200px 0;
        }
        100% {
            background-position: 1200px 0;
        }
    }
    
    @keyframes bounceIn {
        0% {
            opacity: 0;
            transform: scale(0.2) rotate(-45deg);
        }
        50% {
            opacity: 1;
            transform: scale(1.15) rotate(10deg);
        }
        100% {
            opacity: 1;
            transform: scale(1) rotate(0deg);
        }
    }
    
    @keyframes glow {
        0%, 100% {
            box-shadow: 0 0 20px rgba(102, 126, 234, 0.3);
        }
        50% {
            box-shadow: 0 0 40px rgba(102, 126, 234, 0.6);
        }
    }
    
    @keyframes morphBackground {
        0%, 100% {
            border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%;
        }
        50% {
            border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%;
        }
    }
    
    .slick-dots li button:before {
        color: #667eea !important;
        font-size: 12px !important;
        opacity: 0.5 !important;
    }
    
    .slick-dots li.slick-active button:before {
        opacity: 1 !important;
        color: #667eea !important;
    }
`;

// Inject styles
if (typeof document !== 'undefined') {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = globalStyles;
    document.head.appendChild(styleSheet);
}

const FacultySection = ({ endpointsUrl }) => {

    const isMobile = useMediaQuery("(min-width:600px)");

    const [activeIndex, setActiveIndex] = useState(0);
    const [employee, setEmployee] = useState([]);

    const settings = {
        infinite: employee.length > 1, // Prevent infinite loop if only one employee
        speed: 500,
        slidesToShow: Math.min(employee.length, isMobile ? 5 : 1), // Ensure correct count
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 2000,
        arrows: false, // Remove arrows
        beforeChange: (current, next) => setActiveIndex(next),
        responsive: [
            {
                breakpoint: 600,
                settings: {
                    slidesToShow: Math.min(employee.length, 1), // Ensure it doesn't break
                    dots: true,
                    arrows: false, // Remove arrows on mobile too
                },
            },
        ],
    };


    useEffect(() => {
        getEmployee();
    }, []);

    const getEmployee = async () => {
        try {
            const response = await Network.fetchEmployee(instId);
            setEmployee(response?.employees);

        } catch (error) {
            console.error("Error fetching employees:", error);
        }
    };


    return (
        <Box 
            sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                position: 'relative',
                overflow: 'hidden',
                py: { xs: 4, sm: 6, md: 8 },
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '-50%',
                    left: '-50%',
                    width: '200%',
                    height: '200%',
                    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
                    backgroundSize: '50px 50px',
                    animation: 'morphBackground 20s ease-in-out infinite',
                    zIndex: 0,
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: '20%',
                    right: '10%',
                    width: '300px',
                    height: '300px',
                    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                    borderRadius: '50%',
                    animation: 'float 6s ease-in-out infinite',
                    zIndex: 0,
                }
            }}
        >
            {/* Floating Elements */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '10%',
                    left: '5%',
                    width: { xs: 60, sm: 80, md: 100 },
                    height: { xs: 60, sm: 80, md: 100 },
                    background: 'linear-gradient(45deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))',
                    borderRadius: '50%',
                    backdropFilter: 'blur(10px)',
                    animation: 'float 8s ease-in-out infinite',
                    zIndex: 1,
                }}
            />
            
            <Box
                sx={{
                    position: 'absolute',
                    bottom: '15%',
                    right: '5%',
                    width: { xs: 80, sm: 120, md: 150 },
                    height: { xs: 80, sm: 120, md: 150 },
                    background: 'linear-gradient(45deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))',
                    borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
                    backdropFilter: 'blur(15px)',
                    animation: 'morphBackground 12s ease-in-out infinite',
                    zIndex: 1,
                }}
            />

            <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
                {/* Modern Header Section */}
                <Box 
                    sx={{ 
                        textAlign: 'center', 
                        mb: { xs: 4, sm: 6, md: 8 },
                        animation: 'fadeInUp 1s ease-out'
                    }}
                >
                    <Typography
                        variant="h2"
                        sx={{
                            fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
                            fontWeight: 800,
                            background: 'linear-gradient(45deg, #fff 30%, rgba(255,255,255,0.8) 90%)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            textShadow: '0 4px 20px rgba(0,0,0,0.3)',
                            mb: 2,
                            animation: 'slideInLeft 1s ease-out 0.2s both'
                        }}
                    >
                        Meet Our Star Faculty
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                        <Typography
                            variant="h6"
                            sx={{
                                color: 'rgba(255,255,255,0.9)',
                                fontSize: { xs: '1rem', sm: '1.2rem', md: '1.3rem' },
                                fontWeight: 500,
                                maxWidth: '800px',
                                animation: 'fadeInUp 1s ease-out 0.4s both'
                            }}
                        >
                            The Core Team Behind
                        </Typography>
                        {isMobile ? (
                            <Box 
                                sx={{ 
                                    mx: 2,
                                    animation: 'bounceIn 1s ease-out 0.6s both'
                                }}
                            >
                                <img 
                                    src="img/examprep logo 1.png" 
                                    alt="V Smart Logo" 
                                    style={{ 
                                        width: "180px", 
                                        height: "auto",
                                        filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.3))'
                                    }} 
                                />
                            </Box>
                        ) : (
                            <Typography
                                variant="h6"
                                sx={{
                                    color: 'rgba(255,255,255,0.9)',
                                    fontSize: { xs: '1rem', sm: '1.2rem', md: '1.3rem' },
                                    fontWeight: 600,
                                    ml: 1,
                                    animation: 'fadeInUp 1s ease-out 0.6s both'
                                }}
                            >
                                V Smart
                            </Typography>
                        )}
                        <Typography
                            variant="h6"
                            sx={{
                                color: 'rgba(255,255,255,0.9)',
                                fontSize: { xs: '1rem', sm: '1.2rem', md: '1.3rem' },
                                fontWeight: 500,
                                animation: 'fadeInUp 1s ease-out 0.8s both'
                            }}
                        >
                            - Test Series Platform
                        </Typography>
                    </Box>
                    
                    {/* <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 4 }}>
                        {[StarIcon, SchoolIcon, WorkIcon].map((Icon, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: { xs: 40, sm: 50 },
                                    height: { xs: 40, sm: 50 },
                                    background: 'rgba(255,255,255,0.15)',
                                    backdropFilter: 'blur(10px)',
                                    borderRadius: '50%',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    animation: `bounceIn 1s ease-out ${1 + index * 0.2}s both`,
                                    '&:hover': {
                                        background: 'rgba(255,255,255,0.25)',
                                        transform: 'scale(1.1)',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                    }
                                }}
                            >
                                <Icon sx={{ color: 'white', fontSize: { xs: 20, sm: 24 } }} />
                            </Box>
                        ))}
                    </Box> */}
                </Box>

                {/* Faculty Cards Slider */}
                {employee?.length > 0 && (
                    <Box 
                        sx={{ 
                            animation: 'fadeInUp 1s ease-out 1.2s both',
                            '& .slick-slider': {
                                '& .slick-list': {
                                    overflow: 'visible'
                                },
                                '& .slick-track': {
                                    display: 'flex',
                                    alignItems: 'center',
                                }
                            }
                        }}
                    >
                        <Slider className="employee-slider" {...settings}>
                            {employee.map((item, index) => {
                                const joiningDate = dayjs(item.joining);
                                const today = dayjs();
                                const years = today.diff(joiningDate, "year");
                                const months = today.diff(joiningDate, "month") % 12;
                                const experience = `${years} Year${years > 1 ? "s" : ""} ${months} Month${months > 1 ? "s" : ""}`;

                                return (
                                    <Box key={index} sx={{ px: { xs: 1, sm: 2 } }}>
                                        <Card
                                            sx={{
                                                maxWidth: 280,
                                                height: 'auto',
                                                margin: 'auto',
                                                background: 'rgba(255, 255, 255, 0.1)',
                                                backdropFilter: 'blur(30px)',
                                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                                borderRadius: '25px',
                                                overflow: 'hidden',
                                                position: 'relative',
                                                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                                animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`,
                                                '&:hover': {
                                                    transform: 'translateY(-10px) scale(1.02)',
                                                    background: 'rgba(255, 255, 255, 0.15)',
                                                    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3)',
                                                },
                                                '&::before': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: '-100%',
                                                    width: '100%',
                                                    height: '100%',
                                                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                                                    transition: 'left 0.6s',
                                                },
                                                '&:hover::before': {
                                                    left: '100%',
                                                }
                                            }}
                                        >
                                            {/* Profile Image Container */}
                                            <Box
                                                sx={{
                                                    position: 'relative',
                                                    overflow: 'hidden',
                                                    borderRadius: '25px 25px 0 0',
                                                    height: { xs: 300, sm: 400 }
                                                }}
                                            >
                                                <img
                                                    src={item.profile ? endpointsUrl + item.profile : 'img/employee.svg'}
                                                    alt={`${item?.firstName} ${item?.lastName}`}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.target.style.transform = 'scale(1.1)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.target.style.transform = 'scale(1)';
                                                    }}
                                                />
                                                
                                                {/* Gradient Overlay */}
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        bottom: 0,
                                                        left: 0,
                                                        right: 0,
                                                        height: '40%',
                                                        background: 'linear-gradient(transparent 0%, rgba(0,0,0,0.6) 100%)',
                                                    }}
                                                />
                                            </Box>

                                            {/* Faculty Info */}
                                            <Box
                                                sx={{
                                                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.9) 0%, rgba(118, 75, 162, 0.9) 100%)',
                                                    backdropFilter: 'blur(20px)',
                                                    p: { xs: 2, sm: 2.5 },
                                                    borderRadius: '0 0 25px 25px',
                                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                                }}
                                            >
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        color: 'white',
                                                        fontWeight: 700,
                                                        fontSize: { xs: '1.3rem', sm: '1.5rem' },
                                                        mb: 1,
                                                        textAlign: 'center',
                                                        textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                                        lineHeight: 1.2,
                                                    }}
                                                >
                                                    {item?.firstName + ' ' + item?.lastName || 'John Doe'}
                                                </Typography>
                                                
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                                                    <SchoolIcon sx={{ color: 'rgba(255,255,255,0.9)', fontSize: 20, mr: 0.5 }} />
                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            color: 'rgba(255,255,255,0.9)',
                                                            fontWeight: 500,
                                                            fontSize: { xs: '1rem', sm: '1.1rem' },
                                                            textAlign: 'center',
                                                        }}
                                                    >
                                                        {item.designation || 'Faculty'}
                                                    </Typography>
                                                </Box>
                                                
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <WorkIcon sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 20, mr: 0.5 }} />
                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            color: 'rgba(255,255,255,0.8)',
                                                            fontWeight: 400,
                                                            fontSize: { xs: '0.9rem', sm: '1rem' },
                                                            textAlign: 'center',
                                                        }}
                                                    >
                                                        {experience || '12 Year Experience'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Card>
                                    </Box>
                                );
                            })}
                        </Slider>
                    </Box>
                )}

                {/* No Faculty Found Message */}
                {employee?.length === 0 && (
                    <Card
                        sx={{
                            maxWidth: 600,
                            margin: 'auto',
                            background: 'rgba(255, 255, 255, 0.1)',
                            backdropFilter: 'blur(30px)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '20px',
                            p: 4,
                            textAlign: 'center',
                            animation: 'fadeInUp 1s ease-out'
                        }}
                    >
                        <SchoolIcon 
                            sx={{ 
                                fontSize: 80, 
                                color: 'rgba(255,255,255,0.6)', 
                                mb: 2,
                                animation: 'pulse 2s infinite'
                            }} 
                        />
                        <Typography
                            variant="h5"
                            sx={{
                                color: 'white',
                                fontWeight: 600,
                                fontSize: { xs: '1.5rem', sm: '1.8rem' },
                                mb: 1
                            }}
                        >
                            No Faculty Members Found
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'rgba(255,255,255,0.8)',
                                fontSize: { xs: '1.1rem', sm: '1.3rem' }
                            }}
                        >
                            Our amazing faculty team will be available soon!
                        </Typography>
                    </Card>
                )}
            </Container>
        </Box>
    );
};

export default FacultySection;